import { audio } from '../audio/audio';
import { store } from '../game/store';
import { BoardRenderer, type RenderOptions } from '../render/board';

export interface Screen {
  /** Build DOM inside the screen container. */
  mount(root: HTMLElement): void;
  unmount(): void;
  /** Draw a frame on the shared canvas; return true to keep animating. */
  frame(time: number): boolean;
  /** Viewport changed. */
  layout?(): void;
  /** Container should scroll (menus) instead of being a fixed game surface. */
  scroll?: boolean;
  /** Document title suffix. */
  title?: string;
}

class App {
  canvas!: HTMLCanvasElement;
  renderer!: BoardRenderer;
  root!: HTMLElement;
  private screen: Screen | null = null;
  private raf = 0;
  private factory: ((hash: string) => Screen) | null = null;

  init(factory: (hash: string) => Screen) {
    this.factory = factory;
    this.canvas = document.getElementById('sky') as HTMLCanvasElement;
    this.root = document.getElementById('screen') as HTMLElement;
    this.renderer = new BoardRenderer(this.canvas);
    this.applySettings();
    this.resize();
    window.addEventListener('resize', () => this.resize());
    window.addEventListener('hashchange', () => this.route());
    document.addEventListener('visibilitychange', () => {
      audio.setHidden(document.visibilityState === 'hidden');
      if (document.visibilityState === 'visible') this.requestFrame();
    });
    // Audio may only start after a user gesture.
    const unlock = () => audio.unlock();
    document.addEventListener('pointerdown', unlock, true);
    document.addEventListener('keydown', unlock, true);
    store.onChange(() => {
      this.applySettings();
      this.renderer.invalidate();
      this.requestFrame();
    });
    try {
      window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', () => {
        this.applySettings();
        this.requestFrame();
      });
    } catch {
      // Old browsers without matchMedia listeners: the setting still works.
    }
    this.route();
  }

  private applySettings() {
    const s = store.settings;
    const html = document.documentElement;
    html.dataset.contrast = String(s.contrast);
    html.dataset.motion = store.reducedMotion ? 'reduce' : 'full';
    audio.configure(s.sound, s.volume);
  }

  renderOptions(time: number): RenderOptions {
    return {
      time,
      reduced: store.reducedMotion,
      patterns: store.settings.patterns,
      contrast: store.settings.contrast,
    };
  }

  private resize() {
    const dpr = Math.min(2.5, window.devicePixelRatio || 1);
    this.renderer.resize(window.innerWidth, window.innerHeight, dpr);
    this.screen?.layout?.();
    this.requestFrame();
  }

  navigate(hash: string) {
    if (location.hash === hash) this.route();
    else location.hash = hash;
  }

  private route() {
    if (!this.factory) return;
    const next = this.factory(location.hash);
    if (this.screen) this.screen.unmount();
    this.root.replaceChildren();
    this.root.classList.toggle('scroll', !!next.scroll);
    this.root.scrollTop = 0;
    this.screen = next;
    next.mount(this.root);
    document.title = next.title
      ? `${next.title} — Lensing`
      : 'Lensing — bend starlight with gravity';
    // Layout after the DOM is in place.
    requestAnimationFrame(() => {
      next.layout?.();
      this.requestFrame();
    });
    this.requestFrame();
  }

  requestFrame() {
    if (this.raf) return;
    this.raf = requestAnimationFrame((t) => this.tick(t));
  }

  private tick(time: number) {
    this.raf = 0;
    if (!this.screen) return;
    const more = this.screen.frame(time);
    if (more && document.visibilityState === 'visible') this.requestFrame();
  }
}

export const app = new App();

/** Set by the first screen that becomes interactive. */
export function markReady() {
  document.documentElement.dataset.ready = 'true';
}
